import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSecondScreenData } from '../../../../contexts/SecondScreenContext';
import type { TabletopTelemetryView } from '../../../../types/second-screen.types';

const MAX_VISIBLE = 10;
const ARRIVE_HOLD_MS = 400; // matches alert-arrive's duration, just clears the "fresh" flag afterwards
const RETIRE_MS = 400; // matches alert-retire's duration — item stays mounted long enough to finish the animation
const CRITICAL_BRIGHT_HOLD_MS = 15_000;

const SEVERITY_RULE: Record<TabletopTelemetryView['severity'], string> = {
  critical: 'border-red-400',
  warning: 'border-amber-400',
  info: 'border-white/20',
};

function formatSimOffset(atSimMs: number): string {
  const totalMinutes = Math.max(0, Math.floor(atSimMs / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `T+${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

// useTabletopFeed delivers a full snapshot every 5s, not an incremental feed
// (same constraint DecisionTickerWidget already works around), so arrivals
// are detected by diffing seqs against a ref of what's already been seen —
// same pattern, applied here to telemetry instead of decisions. Retirement
// is the mirror case: an item dropping off the MAX_VISIBLE cap gets one more
// render cycle so alert-retire can play instead of the item vanishing.
const AlertStream: React.FC = () => {
  const { state } = useSecondScreenData();
  const telemetry = state?.tabletop?.telemetry ?? [];
  const visible = useMemo(() => [...telemetry].reverse().slice(0, MAX_VISIBLE), [telemetry]);

  const seenSeqsRef = useRef<Set<number>>(new Set());
  const isFirstRun = useRef(true);
  const prevVisibleRef = useRef<TabletopTelemetryView[]>([]);
  const pendingTimeouts = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const [freshSeqs, setFreshSeqs] = useState<Set<number>>(new Set());
  const [brightCriticalSeqs, setBrightCriticalSeqs] = useState<Set<number>>(new Set());
  const [retiring, setRetiring] = useState<TabletopTelemetryView[]>([]);

  useEffect(() => {
    return () => {
      pendingTimeouts.current.forEach((id) => clearTimeout(id));
      pendingTimeouts.current.clear();
    };
  }, []);

  useEffect(() => {
    // Seed on first mount without flagging anything as new — otherwise a
    // screen reload or a widget added mid-run would flash the whole visible
    // window at once, same reasoning as DecisionTickerWidget.
    if (isFirstRun.current) {
      isFirstRun.current = false;
      visible.forEach((a) => seenSeqsRef.current.add(a.seq));
      prevVisibleRef.current = visible;
      return;
    }

    const newlySeen = visible.filter((a) => !seenSeqsRef.current.has(a.seq));
    if (newlySeen.length > 0) {
      newlySeen.forEach((a) => seenSeqsRef.current.add(a.seq));

      setFreshSeqs((prev) => {
        const next = new Set(prev);
        newlySeen.forEach((a) => next.add(a.seq));
        return next;
      });
      newlySeen.forEach((a) => {
        const t = setTimeout(() => {
          pendingTimeouts.current.delete(t);
          setFreshSeqs((prev) => {
            const next = new Set(prev);
            next.delete(a.seq);
            return next;
          });
        }, ARRIVE_HOLD_MS);
        pendingTimeouts.current.add(t);
      });

      const newCritical = newlySeen.filter((a) => a.severity === 'critical');
      if (newCritical.length > 0) {
        setBrightCriticalSeqs((prev) => {
          const next = new Set(prev);
          newCritical.forEach((a) => next.add(a.seq));
          return next;
        });
        newCritical.forEach((a) => {
          const t = setTimeout(() => {
            pendingTimeouts.current.delete(t);
            setBrightCriticalSeqs((prev) => {
              const next = new Set(prev);
              next.delete(a.seq);
              return next;
            });
          }, CRITICAL_BRIGHT_HOLD_MS);
          pendingTimeouts.current.add(t);
        });
      }
    }

    // Anything visible last render but pushed out of the cap this render
    // retires instead of disappearing instantly.
    const visibleSeqs = new Set(visible.map((a) => a.seq));
    const droppedOff = prevVisibleRef.current.filter((a) => !visibleSeqs.has(a.seq));
    if (droppedOff.length > 0) {
      setRetiring((prev) => [...prev, ...droppedOff]);
      droppedOff.forEach((a) => {
        const t = setTimeout(() => {
          pendingTimeouts.current.delete(t);
          setRetiring((prev) => prev.filter((r) => r.seq !== a.seq));
        }, RETIRE_MS);
        pendingTimeouts.current.add(t);
      });
    }
    prevVisibleRef.current = visible;
    // Only re-run when the visible seq list actually changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible.map((a) => a.seq).join(',')]);

  if (visible.length === 0 && retiring.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-white/30" style={{ fontSize: '16px' }}>
        No telemetry yet
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full gap-1.5 overflow-hidden">
      {visible.map((alert) => {
        const isBright = brightCriticalSeqs.has(alert.seq);
        return (
          <div
            key={alert.seq}
            className={`flex-shrink-0 flex items-start gap-2 border-l-4 pl-2 rounded-r ${SEVERITY_RULE[alert.severity] || SEVERITY_RULE.info} ${
              isBright ? 'bg-red-500/20' : ''
            }`}
            style={freshSeqs.has(alert.seq) ? { animation: 'alert-arrive 400ms ease-out' } : undefined}
          >
            <span className="font-mono tabular-nums text-white/40 flex-shrink-0" style={{ fontSize: '16px' }}>
              {formatSimOffset(alert.atSimMs)}
            </span>
            <p
              className={`leading-snug ${alert.severity === 'info' ? 'font-normal text-white/80' : 'font-bold text-white'}`}
              style={{ fontSize: '20px' }}
            >
              {alert.label}
            </p>
          </div>
        );
      })}
      {retiring.map((alert) => (
        <div
          key={alert.seq}
          className={`flex-shrink-0 flex items-start gap-2 border-l-4 pl-2 rounded-r ${SEVERITY_RULE[alert.severity] || SEVERITY_RULE.info}`}
          style={{ animation: 'alert-retire 400ms ease-in forwards' }}
        >
          <span className="font-mono tabular-nums text-white/40 flex-shrink-0" style={{ fontSize: '16px' }}>
            {formatSimOffset(alert.atSimMs)}
          </span>
          <p className="leading-snug text-white/80" style={{ fontSize: '20px' }}>{alert.label}</p>
        </div>
      ))}
    </div>
  );
};

export default AlertStream;
